import classNames from "classnames";
import {addMinutes, addMonths, format, startOfMinute} from "date-fns";

import CloudMonitoringSvg from "@/assets/images/cloud-monitoring.svg";
import MobileAlertsSvg from "@/assets/images/mobile-alerts.svg";
import StatusPageUpSvg from "@/assets/images/status-page-up.svg";
import UptimeChecksPageSvg from "@/assets/images/uptime-checks-page.svg";
import UptimeStatusPageSvg from "@/assets/images/uptime-status-page.svg";
import {CDNImage} from "@/components/cdnImage/CDNImage.tsx";
import {IsItDownServiceStatus, PublicProfile} from "@/lib/types/isitdown.ts";
import {FeatureTabs} from "@/marketingPages/components/featuretabs/FeatureTabs.tsx";

import styles from "./ThirdPartyTabs.module.scss";

interface ThirdPartyTabsProps {
  profile: PublicProfile;
}

export const ThirdPartyTabs = ({profile}: ThirdPartyTabsProps) => {
  const incidentDate = addMonths(new Date(), 1);
  const reportDate = startOfMinute(addMinutes(incidentDate, 5));
  const currentStatus =
    profile.service_status === IsItDownServiceStatus.DOWN ? IsItDownServiceStatus.DOWN : IsItDownServiceStatus.UP; // skip maintenance
  const previousStatus =
    currentStatus === IsItDownServiceStatus.DOWN ? IsItDownServiceStatus.UP : IsItDownServiceStatus.DOWN;
  const profileName = profile.name || profile.slug;

  const tabs = [
    {
      name: "Third Party Monitoring",
      icon: <img src={CloudMonitoringSvg} alt="Third Party Monitoring" />,
      title: `Monitor ${profileName} and other third party cloud services in one place.`,
      description: `Uptime.com monitors ${profileName} along with over 1,200 other cloud services, tracking more than 100,000 outages annually. Gain a comprehensive view of your entire cloud ecosystem and real-time uptime status in one place, enabling quick identification and resolution of incidents caused by third-party outages.`,
      feature: (
        <img
          src={UptimeChecksPageSvg}
          alt={`Monitor ${profileName} and other third party cloud services in one place.`}
        />
      ),
    },
    {
      name: "Status Change Alerts",
      icon: <img src={MobileAlertsSvg} alt="Status Change Alerts" />,
      title: `Get notified when ${profileName} status changes`,
      description: `Set up alerts to trigger instantly when ${profileName} status changes or performance thresholds are breached. Receive real-time notifications via SMS, email, voice call, or through integrations with tools like Slack and PagerDuty.`,
      feature: (
        <div className={styles.container}>
          <div className="pulse-bg-gray-50 py-3 d-flex flex-column align-items-center gap-3">
            <div className={styles.image}>
              <CDNImage
                src={profile.image}
                domain={profile.slug}
                alt={`${profileName} is ${currentStatus}`}
                title={profileName}
              />
            </div>
            <div className="d-flex flex-column gap-2 align-items-center pulse-color-black">
              <div className="marketingTextCaptionBold">
                <span className={styles.capitalize}>{profileName} is</span>{" "}
                <span
                  className={
                    currentStatus === IsItDownServiceStatus.UP ? "pulse-color-green-500" : "pulse-color-red-500"
                  }
                >
                  {currentStatus}
                </span>
              </div>
              <div className="marketingText3XS">
                Previous status was:{" "}
                <span
                  className={classNames(
                    previousStatus === IsItDownServiceStatus.UP ? "pulse-color-green-500" : "pulse-color-red-500",
                    styles.capitalize
                  )}
                >
                  {previousStatus}
                </span>
              </div>
            </div>
          </div>
          <div className="px-3 marketingTextXXS pulse-color-black d-flex flex-column gap-1">
            <div>
              Service: <span className={classNames("marketingTextXXSHeavy", styles.capitalize)}>{profileName}</span>
            </div>
            <div>
              Component: <span className="marketingTextXXSHeavy">API</span>
            </div>
            <div>
              Date & Time: <span className="marketingTextXXSHeavy">{format(reportDate, "PPppp")}</span>
            </div>
          </div>
          <div className="px-3 pb-2 pulse-color-black">
            <div className="mb-2 marketingTextXXS">Description:</div>
            <div className="marketingText3XS">
              {format(incidentDate, "PPppp")}
              <div>
                <span className="marketingText3XSHeavy">
                  {currentStatus === IsItDownServiceStatus.UP ? "Resolved" : "Investigating"}
                </span>{" "}
                -{" "}
                {currentStatus === IsItDownServiceStatus.UP
                  ? "This incident has been resolved."
                  : "Users are experiencing elevated errors for the impacted services."}
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      name: "Status Pages",
      icon: <img src={StatusPageUpSvg} alt="Status Pages" />,
      title: `Add ${profileName} to your status page`,
      description: `Add ${profileName} and other third party services to your public or private status pages. Allow clients to see how your cloud dependencies are performing next to your own custom monitoring checks. Build trust and keep your customers informed, all in one place.`,
      feature: <img src={UptimeStatusPageSvg} alt={`Add ${profileName} to your status page`} />,
    },
  ];

  return <FeatureTabs tabs={tabs} />;
};
